import React, { useState, Children } from 'react';
import { motion } from 'framer-motion';
import { CheckCircleIcon, ClockIcon, StarIcon, InfoIcon, FileTextIcon, SparklesIcon, CreditCardIcon, RefreshCwIcon } from 'lucide-react';
import { TopBar } from '../components/TopBar';
interface NotificationsProps {
  navigate: (screen: string) => void;
}
export function Notifications({
  navigate
}: NotificationsProps) {
  const [readIds, setReadIds] = useState<Set<number>>(new Set());
  const notifications = [{
    id: 1,
    type: 'success',
    icon: CheckCircleIcon,
    iconBg: 'bg-emerald-100',
    iconColor: 'text-emerald-600',
    accentColor: 'bg-emerald-500',
    title: 'Purchase Confirmed',
    message: 'Your Child Education plan has been activated! Your temporary certificate is ready in Policy History.',
    time: '2 hours ago',
    unread: true,
    group: 'today'
  }, {
    id: 7,
    type: 'payment',
    icon: CreditCardIcon,
    iconBg: 'bg-emerald-100',
    iconColor: 'text-emerald-600',
    accentColor: 'bg-emerald-500',
    title: 'Payment Successful',
    message: 'Payment of ₦16,666 received for your Child Education plan. Thank you!',
    time: '3 hours ago',
    unread: true,
    group: 'today'
  }, {
    id: 2,
    type: 'milestone',
    icon: StarIcon,
    iconBg: 'bg-amber-100',
    iconColor: 'text-amber-500',
    accentColor: 'bg-amber-500',
    title: 'Savings Milestone 🎉',
    message: 'Congratulations! You have reached 42% of your ₦10,000,000 education fund goal!',
    time: '1 day ago',
    unread: true,
    group: 'today'
  }, {
    id: 3,
    type: 'document',
    icon: FileTextIcon,
    iconBg: 'bg-purple-100',
    iconColor: 'text-purple-600',
    accentColor: 'bg-purple-500',
    title: 'Final Certificate Ready',
    message: 'Great news! Your final certificate from AXA Mansard is now available in your Document Vault.',
    time: '3 days ago',
    unread: false,
    group: 'earlier'
  }, {
    id: 4,
    type: 'reminder',
    icon: ClockIcon,
    iconBg: 'bg-orange-100',
    iconColor: 'text-orange-600',
    accentColor: 'bg-orange-500',
    title: 'Payment Reminder',
    message: 'Your monthly premium of ₦16,666 for Child Education is due in 3 days. Ensure wallet is funded.',
    time: '4 days ago',
    unread: false,
    group: 'earlier'
  }, {
    id: 5,
    type: 'info',
    icon: InfoIcon,
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-600',
    accentColor: 'bg-blue-500',
    title: 'Claim Status Update',
    message: 'Your motor insurance claim (CLM-8492) is now Under Review by the underwriter.',
    time: '1 week ago',
    unread: false,
    group: 'earlier'
  }, {
    id: 8,
    type: 'renewal',
    icon: RefreshCwIcon,
    iconBg: 'bg-indigo-100',
    iconColor: 'text-indigo-600',
    accentColor: 'bg-indigo-500',
    title: 'Policy Renewal',
    message: 'Your Motor Vehicle Insurance expires on April 15. Renew now to maintain continuous cover.',
    time: '1 week ago',
    unread: false,
    group: 'earlier'
  }, {
    id: 6,
    type: 'promo',
    icon: SparklesIcon,
    iconBg: 'bg-gradient-to-br from-indigo-100 to-purple-100',
    iconColor: 'text-indigo-600',
    accentColor: 'bg-gradient-to-r from-indigo-500 to-purple-500',
    title: 'New Product Alert ✨',
    message: 'Introducing Device Insurance! Protect your smartphone from just ₦500/month.',
    time: '2 weeks ago',
    unread: false,
    group: 'earlier'
  }];
  const isUnread = (notif: (typeof notifications)[0]) => notif.unread && !readIds.has(notif.id);
  const unreadCount = notifications.filter(n => isUnread(n)).length;
  const markAllRead = () => {
    setReadIds(new Set(notifications.filter(n => n.unread).map(n => n.id)));
  };
  const todayNotifs = notifications.filter(n => n.group === 'today');
  const earlierNotifs = notifications.filter(n => n.group === 'earlier');
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.06
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      x: 20
    },
    show: {
      opacity: 1,
      x: 0,
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 24
      }
    }
  };
  const renderNotif = (notification: (typeof notifications)[0]) => {
    const isUnread = notification.unread && !readIds.has(notification.id);
    return <motion.div key={notification.id} variants={itemVariants} className={`bg-white rounded-2xl p-4 border transition-all relative overflow-hidden ${isUnread ? 'border-gray-200 shadow-md' : 'border-gray-100 shadow-sm'}`} data-id="element-564">
        {/* Left accent line */}
        <div className={`absolute left-0 top-0 bottom-0 w-1 ${notification.accentColor} ${isUnread ? 'opacity-100' : 'opacity-30'}`} data-id="element-565"></div>

        <div className="flex gap-4" data-id="element-566">
          <div className="relative shrink-0" data-id="element-567">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${notification.iconBg} ${notification.iconColor}`} data-id="element-568">
              <notification.icon className="w-5 h-5" data-id="element-569" />
            </div>
            {isUnread && <div className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-amber-500 rounded-full border-2 border-white" data-id="element-570"></div>}
          </div>

          <div className="flex-1 pt-1" data-id="element-571">
            <div className="flex justify-between items-start mb-1" data-id="element-572">
              <h3 className={`text-sm font-bold ${isUnread ? 'text-[#1B3B36]' : 'text-gray-700'}`} data-id="element-573">
                {notification.title}
              </h3>
              <span className="text-[10px] font-bold text-gray-400 whitespace-nowrap ml-2" data-id="element-574">
                {notification.time}
              </span>
            </div>
            <p className={`text-xs leading-relaxed ${isUnread ? 'text-gray-600 font-medium' : 'text-gray-500'}`} data-id="element-575">
              {notification.message}
            </p>

            {notification.action && <button className={`mt-3 text-xs font-bold px-4 py-2 rounded-lg transition-colors ${isUnread ? `${notification.accentColor} text-white shadow-sm` : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-576">
                {notification.action}
              </button>}
          </div>
        </div>
      </motion.div>;
  };
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="flex-1 overflow-y-auto pb-24 bg-slate-50" data-id="element-577">
      <TopBar title="Notifications" showBack onBack={() => navigate('dashboard')} data-id="element-578" />

      <div className="px-5 pt-4 pb-6" data-id="element-579">
        <div className="flex justify-between items-center mb-6" data-id="element-580">
          <h2 className="text-xl font-bold text-[#1B3B36]" data-id="element-581">Recent</h2>
          <button onClick={() => setReadIds(new Set(notifications.map(n => n.id)))} className="text-xs font-bold text-gray-500 hover:text-[#1B3B36] transition-colors flex items-center gap-1" data-id="element-582">
            <CheckCircleIcon className="w-3.5 h-3.5" data-id="element-583" /> Mark all read
          </button>
        </div>

        <motion.div variants={containerVariants} initial="hidden" animate="show" className="space-y-3 mb-6" data-id="element-584">
          {todayNotifs.map(renderNotif)}
        </motion.div>

        <motion.div variants={containerVariants} initial="hidden" animate="show" className="space-y-3" data-id="element-585">
          {earlierNotifs.map(renderNotif)}
        </motion.div>
      </div>
    </motion.div>;
}